import React from 'react';
import type { CatalogProperty } from 'src/api/generated/models/CatalogProperty';
import { TextSkeleton } from 'src/modules/shared/components/atoms/text/text-skeleton';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { PropertyMeliPublishingModal } from '../molecules/property-meli-publishing-modal';
import { PropertyMeliPublishingManager } from './property-meli-publishing-manager';
import { usePropertyPublishingPanel } from '../../hooks/use-property-publishing-panel';
import { PropertyPublishingEmptyPrompt } from './property-publishing-empty-prompt';
import { PropertyPublishingPanelBody } from './property-publishing-panel-body';
import { PropertyPublicationInfoBadge } from '../molecules/property-publication-info-badge';
import { getPropertyPublicationStatusDisplay } from '../../utils/property-publication-display.util';

interface PropertyPublishingPanelProps {
  property: CatalogProperty | null;
}

/**
 * Right-rail publishing summary and entry point for portal-specific workflows.
 */
export const PropertyPublishingPanel: React.FC<
  PropertyPublishingPanelProps
> = ({ property }) => {
  const { t } = useI18n();
  const {
    connection,
    items,
    loading,
    modal,
    permissions,
    publications,
    queued,
    repairSync,
    getQueuedDisplayStatus,
  } = usePropertyPublishingPanel({ property });

  return (
    <aside className="sticky top-4 self-start">
      {!property ? (
        <PropertyPublishingEmptyPrompt
          descriptionKey="catalog.properties.editor.publishing.saveFirstDescription"
          titleKey="catalog.properties.editor.publishing.saveFirstTitle"
          t={t}
        />
      ) : connection.loadingIntegrations ? (
        <div className="flex flex-col gap-3">
          <TextSkeleton className="h-16 rounded-xl" />
        </div>
      ) : loading || permissions.loadingPermissions ? (
        <div className="flex flex-col gap-5">
          <TextSkeleton className="h-20 rounded-xl" />
          <TextSkeleton className="h-16 rounded-xl" />
          <TextSkeleton className="h-16 rounded-xl" />
        </div>
      ) : (
        <PropertyPublishingPanelBody
          connection={connection}
          items={items}
          property={property}
          publications={publications.publications}
          sourceProviderId={connection.sourceProviderId}
          repairSync={{
            canRepairSync: permissions.canRepairSync,
            loading: repairSync.loading,
            onRepairSync: repairSync.handleRepairSync,
          }}
          t={t}
        />
      )}

      {property &&
      permissions.canManagePublications &&
      modal.managerOperation ? (
        <PropertyMeliPublishingModal
          isOpen
          onClose={() => {
            modal.setManagerOperation(null);
            modal.setManagerPublicationId(null);
          }}
          operation={modal.managerOperation}
          headerStatus={
            <PropertyPublicationInfoBadge
              display={getPropertyPublicationStatusDisplay({
                publication: modal.currentManagerPublication,
                status: getQueuedDisplayStatus({
                  publication: modal.currentManagerPublication,
                  queuedAction: queued.action,
                  operation: modal.managerOperation,
                }),
              })}
            />
          }
          body={
            <PropertyMeliPublishingManager
              property={property}
              selectedOperation={modal.managerOperation}
              selectedPublicationId={modal.managerPublicationId}
              publications={publications.publications}
              refetchPublications={async () => {
                await publications.refetchPublications();
              }}
              setQueuedAction={queued.setQueuedAction}
              isSyncMode={connection.isMeliSyncMode}
              showHeader={false}
            />
          }
        />
      ) : null}
    </aside>
  );
};
